// client/src/user/free-tokens/FreeTokens.js
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  alpha,
  useTheme,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  Token as TokenIcon,
  Instagram as InstagramIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Cancel as RejectedIcon,
  OpenInNew as OpenInNewIcon,
  Send as SendIcon,
  CardGiftcard as GiftIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import CommonDialog from "../../common/CommonDialog";

// ── StepCard ───────────────────────────────────────────────────────────────
const StepCard = ({ number, title, desc, color, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(color, 0.25)}`,
        bgcolor: alpha(color, 0.04),
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: alpha(color, 0.15),
            border: `1.5px solid ${alpha(color, 0.4)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color }}>
            {number}
          </Typography>
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ color, mb: 0.25, fontSize: "0.8rem" }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.5 }}
          >
            {desc}
          </Typography>
          {children && <Box mt={1}>{children}</Box>}
        </Box>
      </Stack>
    </Box>
  );
};

// ── FreeTokensContent (the actual UI inside dialog) ────────────────────────
const FreeTokensContent = ({ lang: propLang, userData, onClose }) => {
  const theme = useTheme();
  const { hitAxios } = useContext(GlobalContext);
  const { lang: ctxLang } = useContext(TranslateContext);
  const t = (k) => propLang?.[k] || ctxLang?.[k] || k;

  const [claim, setClaim] = useState(null);
  const [settings, setSettings] = useState({});
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    fetchStatus();
  }, []);

  async function fetchStatus(isRefresh = false) {
    if (isRefresh) setRefreshing(true);
    else setFetching(true);

    const res = await hitAxios({
      path: "/api/instagram/user/claim-status",
      post: false,
      showLoading: false,
      showSnackbar: false,
    });

    if (isRefresh) setRefreshing(false);
    else setFetching(false);

    if (res?.data?.success) {
      setClaim(res.data.claim);
      setSettings(res.data.settings || {});
    }
  }

  async function handleSubmit() {
    if (!username.trim()) return;
    setErrorMsg("");
    setLoading(true);

    const res = await hitAxios({
      path: "/api/instagram/user/submit-claim",
      post: true,
      obj: { instagram_username: username.trim() },
      showSnackbar: false,
    });

    setLoading(false);

    if (res?.data) {
      if (
        res.data.auto_approved ||
        res.data.success ||
        res.data.needs_dm ||
        res.data.not_following
      ) {
        await fetchStatus();
        setUsername("");
        return;
      }
      setErrorMsg(res.data.msg || "Something went wrong");
    }
  }

  const instagramUrl = settings?.page_name
    ? `https://www.instagram.com/${settings.page_name}/`
    : "https://www.instagram.com/codeyon_itservices/";

  // ── Loading ──────────────────────────────────────────────────────────────
  if (fetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} sx={{ color: "primary.main" }} />
      </Box>
    );
  }

  // ── Approved ─────────────────────────────────────────────────────────────
  if (claim?.status === "approved") {
    return (
      <Stack spacing={2.5} sx={{ p: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CheckCircleIcon sx={{ fontSize: 20, color: "success.main" }} />
          <Typography variant="body1" fontWeight={700} color="success.main">
            Tokens Activated! 🎉
          </Typography>
        </Stack>

        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            textAlign: "center",
            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            bgcolor: alpha(theme.palette.success.main, 0.06),
          }}
        >
          <TokenIcon sx={{ fontSize: 36, color: "success.main", mb: 1 }} />
          <Typography variant="h4" fontWeight={800} color="success.main">
            {userData?.daily_token_limit > 0
              ? Number(userData.daily_token_limit).toLocaleString()
              : settings?.daily_token_reward || 100}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            tokens / day
          </Typography>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.7 }}
        >
          Your daily token limit is active. Enjoy using the AI — your limit
          resets every day!
        </Typography>

        <Stack direction="row" alignItems="center" spacing={0.75}>
          <InstagramIcon sx={{ fontSize: 15, color: "#E1306C" }} />
          <Typography variant="caption" color="text.disabled">
            @{claim.instagram_username}
          </Typography>
        </Stack>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Start Chatting
        </Button>
      </Stack>
    );
  }

  // ── Pending — DM instructions ─────────────────────────────────────────────
  if (claim?.status === "pending") {
    return (
      <Stack spacing={2} sx={{ p: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PendingIcon sx={{ fontSize: 20, color: "warning.main" }} />
          <Typography variant="body1" fontWeight={700} color="warning.main">
            Almost there — one more step!
          </Typography>
        </Stack>

        {/* Step 1 — Follow */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.05),
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            color="text.primary"
            mb={1}
          >
            1️⃣ Follow us on Instagram
          </Typography>
          <Button
            size="small"
            variant="outlined"
            href={instagramUrl}
            target="_blank"
            component="a"
            startIcon={<InstagramIcon sx={{ fontSize: 14 }} />}
            endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              fontSize: "0.78rem",
              borderColor: "#E1306C",
              color: "#E1306C",
              "&:hover": { bgcolor: alpha("#E1306C", 0.06) },
            }}
          >
            {settings?.page_name ? `@${settings.page_name}` : "Follow Now"}
          </Button>
        </Box>

        {/* Step 2 — DM */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
            bgcolor: alpha(theme.palette.info.main, 0.04),
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            color="text.primary"
            mb={0.75}
          >
            2️⃣ Send us a DM on Instagram
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1}
            sx={{ lineHeight: 1.7 }}
          >
            Open Instagram and DM us — this lets us verify your follow status
            automatically. No waiting, no manual review.
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              borderRadius: 1.5,
              display: "inline-block",
              bgcolor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "info.main",
                fontFamily: "monospace",
              }}
            >
              "free tokens"
            </Typography>
          </Box>
        </Box>

        {/* Step 3 — Come back */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
            bgcolor: alpha(theme.palette.success.main, 0.04),
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            color="text.primary"
            mb={0.75}
          >
            3️⃣ Come back and hit the button below
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.7 }}
          >
            Once you've DMed us, click <strong>"Check Now"</strong> below. If
            you follow us, tokens activate instantly — no waiting!
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" spacing={0.75}>
          <InstagramIcon sx={{ fontSize: 13, color: "#E1306C" }} />
          <Typography variant="caption" color="text.disabled">
            Submitted as: @{claim.instagram_username}
          </Typography>
        </Stack>

        {/* Refresh button — hits server */}
        <Button
          fullWidth
          variant="contained"
          size="medium"
          disabled={refreshing}
          onClick={() => fetchStatus(true)}
          startIcon={
            refreshing ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <RefreshIcon sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.85rem",
            py: 1,
          }}
        >
          {refreshing ? "Checking..." : "✅ I've Followed and DMed — Check Now"}
        </Button>
      </Stack>
    );
  }

  // ── Rejected ──────────────────────────────────────────────────────────────
  if (claim?.status === "rejected") {
    return (
      <Stack spacing={2} sx={{ p: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <RejectedIcon sx={{ fontSize: 20, color: "error.main" }} />
          <Typography variant="body1" fontWeight={700} color="error.main">
            Not Following Yet
          </Typography>
        </Stack>

        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
            bgcolor: alpha(theme.palette.error.main, 0.04),
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.7 }}
          >
            We checked and <strong>@{claim.instagram_username}</strong> doesn't
            follow us yet. Follow our Instagram, send us a DM saying{" "}
            <strong>"free tokens"</strong>, then try again.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          href={instagramUrl}
          target="_blank"
          component="a"
          startIcon={<InstagramIcon sx={{ fontSize: 15 }} />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.8rem",
            borderColor: "#E1306C",
            color: "#E1306C",
            "&:hover": { bgcolor: alpha("#E1306C", 0.06) },
          }}
        >
          Follow Now on Instagram
        </Button>

        <Button
          variant="text"
          size="small"
          onClick={() => {
            setClaim(null);
            setUsername(claim.instagram_username || "");
          }}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.78rem",
            color: "text.disabled",
          }}
        >
          Try with a different username
        </Button>
      </Stack>
    );
  }

  // ── Default: form ─────────────────────────────────────────────────────────
  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <StepCard
        number="1"
        title="Follow Us on Instagram"
        desc="Follow our Instagram account to qualify"
        color={theme.palette.primary.main}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<InstagramIcon sx={{ fontSize: 14 }} />}
          endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
          href={instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          component="a"
          sx={{
            borderRadius: 1.5,
            textTransform: "none",
            fontSize: "0.78rem",
            borderColor: "#E1306C",
            color: "#E1306C",
            "&:hover": {
              borderColor: "#C13584",
              bgcolor: alpha("#E1306C", 0.06),
            },
          }}
        >
          {settings?.page_name ? `@${settings.page_name}` : "Follow Now"}
        </Button>
      </StepCard>

      <StepCard
        number="2"
        title="Send Us a DM"
        desc='Message us on Instagram saying "free tokens" so we can verify you automatically'
        color={theme.palette.warning.main}
      >
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderRadius: 1.5,
            display: "inline-block",
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "warning.main",
              fontFamily: "monospace",
              letterSpacing: "0.05em",
            }}
          >
            "free tokens"
          </Typography>
        </Box>
      </StepCard>

      <StepCard
        number="3"
        title="Enter Your Username"
        desc="Submit your Instagram username — if you follow us it approves instantly!"
        color={theme.palette.success.main}
      >
        <Stack spacing={1} mt={0.5}>
          <TextField
            fullWidth
            size="small"
            placeholder="your_username (without @)"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value.replace("@", ""));
              setErrorMsg("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            InputProps={{
              startAdornment: (
                <Typography
                  sx={{ color: "text.disabled", fontSize: "0.85rem", mr: 0.25 }}
                >
                  @
                </Typography>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                fontSize: "0.85rem",
              },
            }}
          />

          {errorMsg && (
            <Typography variant="caption" color="error.main">
              ⚠️ {errorMsg}
            </Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            size="medium"
            onClick={handleSubmit}
            disabled={!username.trim() || loading}
            startIcon={
              loading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SendIcon sx={{ fontSize: 15 }} />
              )
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.85rem",
              py: 1,
            }}
          >
            {loading ? "Checking..." : "Submit & Check"}
          </Button>
        </Stack>
      </StepCard>
    </Stack>
  );
};

// ── FreeTokens — exported component with dialog trigger ───────────────────
const FreeTokens = ({ lang: propLang, userData, open, onClose }) => {
  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      title="Get Free Tokens"
      subtitle="Follow us on Instagram → DM us → Tokens activate instantly"
      icon={GiftIcon}
      maxWidth="xs"
      fullWidth
      noMargin={false}
    >
      <FreeTokensContent
        lang={propLang}
        userData={userData}
        onClose={onClose}
      />
    </CommonDialog>
  );
};

export default FreeTokens;
